import { RootState } from '@next/core-lib/app';
import { EntityState } from '@ngrx/entity';
import { Barn } from 'src/app/settings/models/barn.model';

export interface CycleModuleState extends RootState {
  cycles?: CyclesState;
  propagationCompanies: PropagationCompany[];
  hatcheries: Hatchery[];
  selectedCycleId: number;
  selectedOrderId?: number;
  selectedSectionId?: number;
  selectedArrivalId?: number;
  selectedMovementId?: number;
  selectedDepartureId?: number;
  isLoaded: boolean;
}

export type CyclesState = EntityState<Cycle>;

export interface CyclesResponse {
  cycles: Cycle[];
}

export interface UpsertCycleResponse {
  id: number;
}

export interface Cycle {
  id: number;
  cycleNumber: number;
  description: string;
  orders: Order[];
  sections: Section[];
  arrivals: Arrival[];
  movements: Movement[];
  departures: Departure[];
  observationSettings?: ObservationSettings;
}

export interface Order {
  id: number;
  propagationCompany: PropagationCompany;
  breed: Breed;
  arrivalDate: string;
  numberOfMales?: number;
  numberOfFemales?: number;
  isFulFilled: boolean;
}

export interface Section {
  id: number;
  barn: Barn;
  name: string;
  surfaceArea: number;
  breed: Breed;
  gender: Gender;
  referenceValueSheetId?: number;
}

export interface Arrival {
  id: number;
  arrivalDate: string;
  chickBirthDate: string;
  hatchery: Hatchery;
  gpsNumber: string;
  gpsBirthDate: string;
  gpsCountry: string;
  breed: Breed;
  gender: Gender;
  numberOfAnimals: number;
  allocations: ArrivalAllocation[];
}

export interface Movement {
  id: number;
  movementDate: string;
  fromSection: Section;
  fromPropagationCompany: PropagationCompany;
  toSection: Section;
  toPropagationCompany: PropagationCompany;
  numberOfAnimals: number;
}

export interface Departure {
  id: number;
  departureDate: string;
  propagationCompany: PropagationCompany;
  breed: Breed;
  gender: Gender;
  dateOfMeasurement: string;
  allocations: DepartureAllocation[];
}

export interface PropagationCompany {
  id: number;
  externalId: string;
  system: string;
  country: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface ArrivalAllocation {
  id: number;
  section: Section;
  numberOfAnimals: number;
  propagationCompany: PropagationCompany;
}

export interface ArrivalAllocationTableRow {
  id: number;
  sectionId: number;
  sectionName: string;
  propagationCompanyId: number;
  propagationCompanyName: string;
  numberOfAnimals: number;
}

export interface DepartureAllocation {
  id: number;
  section: Section;
  numberOfAnimals: number;
}

export interface DepartureAllocationTableRow {
  id: number;
  sectionId: number;
  sectionName: string;
  numberOfAnimals: number;
}

export interface Breed {
  id: number;
  name: string;
}

export interface Hatchery {
  id: number;
  externalId: string;
  name: string;
  postalCode: string;
  city: string;
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
}

export interface PropagationCompaniesResponse {
  propagationCompanies: PropagationCompany[];
}
export interface HatcheriesResponse {
  hatcheries: Hatchery[];
}

export enum ObservationSettingEnum {
  Daily = 'Daily',
  Off = 'Off',
}

export interface ObservationSettings {
  animalWeightInGrams: ObservationSettingEnum;
  uniformity: ObservationSettingEnum;
  coefficientOfVariation: ObservationSettingEnum;
  feedName: ObservationSettingEnum;
  feedIntakeInGramsPerAnimal: ObservationSettingEnum;
  mortality: ObservationSettingEnum;
  waterIntakeInLitersTotal: ObservationSettingEnum;
  numberOfMinutesLight: ObservationSettingEnum;
  lightIntensityInLux: ObservationSettingEnum;
  notes: ObservationSettingEnum;
}

export interface AnimalTransition {
  fromSectionId?: number;
  toSectionId?: number;
  fromPropagationCompanyId?: number;
  toPropagationCompanyId?: number;
  numberOfAnimals: number;
  date: Date;
  dateString: string;
}
